import { ContactForm } from '../types'

// 使用免费的FormSubmit服务发送邮件
export const sendEmailViaFormSubmit = async (formData: ContactForm, language: string = 'en'): Promise<boolean> => {
  try {
    // FormSubmit是一个免费的表单到邮件服务
    // 只需要POST到他们的端点即可
    const endpoint = 'https://formsubmit.co/rfidlib@libmj.com'
    
    // 根据语言获取邮件内容
    const emailContent = getEmailContent(formData, language)
    
    const formDataToSend = new FormData()
    formDataToSend.append('name', formData.name)
    formDataToSend.append('email', formData.email)
    formDataToSend.append('company', formData.company || '')
    formDataToSend.append('phone', formData.phone || '')
    formDataToSend.append('message', formData.message)
    formDataToSend.append('_subject', emailContent.subject)
    formDataToSend.append('_captcha', 'false') // 禁用验证码
    formDataToSend.append('_template', 'table') // 使用表格模板
    formDataToSend.append('_next', window.location.origin + '?form=success') // 成功后跳转
    
    const response = await fetch(endpoint, {
      method: 'POST',
      body: formDataToSend
    })
    
    return response.ok
  } catch (error) {
    console.error('FormSubmit failed:', error)
    return false
  }
}

// 使用FormSubmit发送到多个邮箱（并行发送）
export const sendEmailToMultipleRecipients = async (formData: ContactForm, language: string = 'en'): Promise<boolean> => {
  try {
    console.log('🔄 Sending to multiple recipients...')
    
    // 目标邮箱列表
    const recipients = [
      'rfidlib@libmj.com',      // 主邮箱
      'wx19138222643@qq.com',      // 备用邮箱1
      '784213999@qq.com'           // 备用邮箱2
    ]
    
    // 根据语言获取邮件内容
    const emailContent = getEmailContent(formData, language)
    
    // 并行发送到所有邮箱
    const sendPromises = recipients.map(async (email) => {
      try {
        console.log(`📤 Sending to ${email}...`)
        
        const endpoint = `https://formsubmit.co/${email}`
        
        const formDataToSend = new FormData()
        formDataToSend.append('name', formData.name)
        formDataToSend.append('email', formData.email)
        formDataToSend.append('company', formData.company || '')
        formDataToSend.append('phone', formData.phone || '')
        formDataToSend.append('message', formData.message)
        formDataToSend.append('_subject', emailContent.subject)
        formDataToSend.append('_captcha', 'false')
        formDataToSend.append('_template', 'table')
        formDataToSend.append('_next', window.location.origin + '?form=success')
        
        const response = await fetch(endpoint, {
          method: 'POST',
          body: formDataToSend
        })
        
        if (response.ok) {
          console.log(`✅ Successfully sent to ${email}`)
          return { email, success: true }
        } else {
          console.warn(`❌ Failed to send to ${email}`)
          return { email, success: false }
        }
      } catch (error) {
        console.error(`❌ Error sending to ${email}:`, error)
        return { email, success: false }
      }
    })
    
    // 等待所有发送完成
    const results = await Promise.all(sendPromises)
    
    // 统计结果
    const successCount = results.filter(result => result.success).length
    const totalCount = results.length
    
    console.log(`📊 Multi-email sending results: ${successCount}/${totalCount} successful`)
    results.forEach(result => {
      console.log(`  ${result.success ? '✅' : '❌'} ${result.email}`)
    })
    
    // 只要有一个成功就算成功
    return successCount > 0
    
  } catch (error) {
    console.error('Multi-email sending failed:', error)
    return false
  }
}

// 使用Web3Forms免费服务（备用方案）
export const sendEmailViaWeb3Forms = async (formData: ContactForm, language: string = 'en'): Promise<boolean> => {
  try {
    // Web3Forms提供每月250封免费邮件
    const accessKey = 'a9b5c8e8-f472-4e54-a087-7b9c35b4f2e3' // 这是一个示例key，需要注册获取
    
    const emailContent = getEmailContent(formData, language)
    
    const response = await fetch('https://api.web3forms.com/submit', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({
        access_key: accessKey,
        name: formData.name,
        email: formData.email,
        company: formData.company || 'Not provided',
        phone: formData.phone || 'Not provided',
        message: formData.message,
        subject: emailContent.subject,
        from_name: 'MiJin Technology Website',
        to: 'rfidlib@libmj.com',
        cc: '', // 可以添加抄送
        bcc: '', // 可以添加密送
        replyto: formData.email,
        redirect: window.location.origin + '?form=success'
      })
    })
    
    const result = await response.json()
    return result.success === true
  } catch (error) {
    console.error('Web3Forms failed:', error)
    return false
  }
}

// 使用Netlify Forms（如果部署在Netlify）
export const sendEmailViaNetlify = async (formData: ContactForm, language: string = 'en'): Promise<boolean> => {
  try {
    const emailContent = getEmailContent(formData, language)
    
    const formDataToSend = new FormData()
    formDataToSend.append('form-name', 'contact')
    formDataToSend.append('name', formData.name)
    formDataToSend.append('email', formData.email)
    formDataToSend.append('company', formData.company || '')
    formDataToSend.append('phone', formData.phone || '')
    formDataToSend.append('message', formData.message)
    formDataToSend.append('subject', emailContent.subject)
    
    const response = await fetch('/', {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: new URLSearchParams(formDataToSend as any).toString()
    })
    
    return response.ok
  } catch (error) {
    console.error('Netlify Forms failed:', error)
    return false
  }
}

// 多语言邮件内容生成
export const getEmailContent = (formData: ContactForm, language: string = 'en') => {
  const translations = {
    en: {
      subject: `New Contact Form Submission from ${formData.name} - MiJin Technology`,
      greeting: 'Dear MiJin Technology Team,',
      intro: 'You have received a new contact form submission from your website.',
      details: 'Contact Details:',
      name: 'Name',
      email: 'Email',
      company: 'Company/Organization',
      phone: 'Phone',
      message: 'Message',
      timestamp: 'Submitted at',
      footer: 'This email was sent automatically from your MiJin Technology website contact form.',
      notProvided: 'Not provided'
    },
    zh: {
      subject: `新的联系表单提交 - ${formData.name} - 米津科技`,
      greeting: '亲爱的米津科技团队：',
      intro: '您收到了一个来自网站的新联系表单提交。',
      details: '联系详情：',
      name: '姓名',
      email: '邮箱',
      company: '公司/组织',
      phone: '电话',
      message: '消息',
      timestamp: '提交时间',
      footer: '此邮件由米津科技网站联系表单自动发送。',
      notProvided: '未提供'
    },
    ja: {
      subject: `新しいお問い合わせ - ${formData.name}様 - MiJin Technology`,
      greeting: 'MiJin Technologyチーム様',
      intro: 'ウェブサイトから新しいお問い合わせフォームの送信がありました。',
      details: 'お問い合わせ詳細：',
      name: '氏名',
      email: 'メールアドレス',
      company: '会社・組織',
      phone: '電話番号',
      message: 'メッセージ',
      timestamp: '送信日時',
      footer: 'このメールはMiJin Technologyウェブサイトのお問い合わせフォームから自動送信されました。',
      notProvided: '未入力'
    },
    es: {
      subject: `Nueva consulta de contacto de ${formData.name} - MiJin Technology`,
      greeting: 'Estimado equipo de MiJin Technology:',
      intro: 'Ha recibido una nueva consulta desde el formulario de contacto de su sitio web.',
      details: 'Detalles del contacto:',
      name: 'Nombre',
      email: 'Correo electrónico',
      company: 'Empresa/Organización',
      phone: 'Teléfono',
      message: 'Mensaje',
      timestamp: 'Enviado el',
      footer: 'Este correo fue enviado automáticamente desde el formulario de contacto de MiJin Technology.',
      notProvided: 'No proporcionado'
    },
    fr: {
      subject: `Nouveau formulaire de contact de ${formData.name} - MiJin Technology`,
      greeting: 'Chère équipe MiJin Technology,',
      intro: 'Vous avez reçu une nouvelle soumission de formulaire de contact depuis votre site web.',
      details: 'Détails du contact:',
      name: 'Nom',
      email: 'Email',
      company: 'Entreprise/Organisation',
      phone: 'Téléphone',
      message: 'Message',
      timestamp: 'Soumis le',
      footer: 'Cet email a été envoyé automatiquement depuis le formulaire de contact de MiJin Technology.',
      notProvided: 'Non fourni'
    }
  }
  
  const t = translations[language as keyof typeof translations] || translations.en
  const timestamp = new Date().toLocaleString(language === 'zh' ? 'zh-CN' : language, {
    timeZone: 'Asia/Shanghai',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  })
  
  return {
    subject: t.subject,
    body: `${t.greeting}

${t.intro}

${t.details}
• ${t.name}: ${formData.name}
• ${t.email}: ${formData.email}  
• ${t.company}: ${formData.company || t.notProvided}
• ${t.phone}: ${formData.phone || t.notProvided}

${t.message}:
${formData.message}

${t.timestamp}: ${timestamp}

---
${t.footer}`
  }
}

// 主要的免费邮件发送函数，带有多重备用方案
export const sendFreeEmail = async (formData: ContactForm, language: string = 'en'): Promise<boolean> => {
  console.log(`📧 Sending email in language: ${language}`)
  
  // 首先尝试多邮箱发送（FormSubmit）
  try {
    console.log('🔄 Trying multi-email sending...')
    const multiEmailSuccess = await sendEmailToMultipleRecipients(formData, language)
    if (multiEmailSuccess) {
      console.log('✅ Multi-email sent successfully')
      return true
    }
  } catch (error) {
    console.warn('Multi-email sending failed:', error)
  }
  
  // 然后尝试Web3Forms
  try {
    console.log('🔄 Trying Web3Forms...')
    const web3FormsSuccess = await sendEmailViaWeb3Forms(formData, language)
    if (web3FormsSuccess) {
      console.log('✅ Email sent successfully via Web3Forms')
      return true
    }
  } catch (error) {
    console.warn('Web3Forms failed:', error)
  }
  
  // 最后尝试Netlify Forms（如果适用）
  try {
    console.log('🔄 Trying Netlify Forms...')
    const netlifySuccess = await sendEmailViaNetlify(formData, language)
    if (netlifySuccess) {
      console.log('✅ Email sent successfully via Netlify Forms')
      return true
    }
  } catch (error) {
    console.warn('Netlify Forms failed:', error)
  }
  
  console.error('❌ All email services failed')
  return false
}
